"use client";

import {
  Box,
  Text,
  Columns,
  Row,
  Button,
  DropdownContent,
  DropdownMenu,
  DropdownTrigger,
} from "@dxp-feed/ui";
import Link from "@/components/Link";
import { IListStores } from "@/utils/redux/storeLocator";
import { useEffect, useState } from "@/utils/hooks/reactUseCustomHooks";
import {
  // createAlgoliaClient,
  handleRedirectOnMap,
  getOpeningHours,
  formatHours,
  isToday,
  formatPhoneNumber,
  constructAllFilterQuery,
} from "@/helpers/storeLocatorHelper";
import useEqualHeight from "@/utils/hooks/useEqualHeight";
import { ALGOLIA_INDEXES } from "@/services/algolia/config";
import { algolia as algoliaClientInst } from "@/services/algolia";
import NearByStoreSkeleton from "./NearByStoreSkeleton";
interface INearByStoresProps {
  store: IListStores | null;
  selectedStore: IListStores;
  openDialog: () => void;
  setStoreToConfirm: (store: IListStores) => void;
}
const NearByStores: React.FC<INearByStoresProps> = ({
  store,
  selectedStore,
  openDialog,
  setStoreToConfirm,
}) => {
  useEqualHeight([".nearstore h2"]);
  const [listStores, setListStores] = useState<IListStores[]>([]);
  const [isLoading, setIsloading] = useState(true);

  const fetchNearByStores = async () => {
    try {
      const stores = await algoliaClientInst.storeSearchByLatLng({
        lat: store?._geoloc?.lat ?? 0,
        lng: store?._geoloc?.lng ?? 0,
        indexName: ALGOLIA_INDEXES.STORES.PRIMARY_INDEX ?? "",
        query: "",
        page: 0,
        hitsPerPage: 10,
        filters: constructAllFilterQuery([], [], []),
        facets: ["departments.type", "address.province"],
      } as any);
      const filteredStores = stores?.hits.filter(
        (nearbyStore: any) => nearbyStore.storeNumber !== store?.storeNumber
      );
      setListStores(filteredStores as IListStores[]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsloading(false);
    }
  };

  useEffect(() => {
    if (store) {
      fetchNearByStores().catch(() => {});
    }
  }, [store, selectedStore]);

  return (
    <>
      {isLoading ? (
        <NearByStoreSkeleton />
      ) : listStores.length > 0 ? (
        <Box>
          <Text
            as="h2"
            className="font-sans lgs:text-2xl text-xl lgs:pb-6 pb-4 lgs:pt-12 pt-6 font-extrabold "
          >
            Nearby Stores
          </Text>
          <Row className="overflow-x-scroll pb-8 mb-6 p-2">
            <Box>
              <Columns className="col-12 flex flex-shrink-0 xl:mb-0 mb-4 nearstore">
                {listStores.slice(0, 3).map((store, index) => {
                  const formatedHours = getOpeningHours(
                    store?.hours?.storeHours || {}
                  );
                  const todayHours =
                    formatedHours?.find((hour) => isToday(hour?.day)) ||
                    formatedHours[0];
                  return (
                    <Box
                      key={index}
                      className="lgs:w-[382px] relative mr-6 last:mr-0 flex-shrink-0  md:w-[357px] xs:w-[432px] w-[350px] rounded-2xl shadow-cardShadow p-6 nearstore"
                    >
                      <Box className="flex h-full flex-col justify-between">
                        <Box className="flex-1">
                          <h2 className="pb-4 ">
                            <Link
                              href={`/stores/${store?.slug}`}
                              className="font-sans text-xl font-extrabold"
                            >
                              {store?.locationName}
                            </Link>
                          </h2>

                          {/* Store Hours */}
                          <Box
                            as="div"
                            className="!text-body pb-4 flex gap-[8px] items-center"
                          >
                            <Box className="[&_.icon-alarm]:before:w-6 [&_.icon-alarm]:before:h-6 [&_.icon-alarm]:before:content-['\e803']">
                              <Text
                                as="span"
                                className="icon-alarm flex text-xl w-6 h-6 justify-center items-center text-[#35A131]"
                              />
                            </Box>
                            {todayHours?.day}{" "}
                            <DropdownMenu>
                              <DropdownTrigger
                                className="flex items-center gap-2 relative w-[160px]"
                                asChild
                              >
                                <Text className="relative bottom-[3px]">.</Text>{" "}
                                {store?.hours?.is24Hours
                                  ? "Open today "
                                  : formatHours(todayHours?.time)}
                                <Text
                                  as="span"
                                  className="icon-arrow text-[7px] cursor-pointer"
                                />
                              </DropdownTrigger>
                              <DropdownContent className="w-[270px] min-w-[270px] absolute right-[-8px] left-auto bottom-auto !text-left p-4 shadow-[0_0_4px_0_rgba(0,0,0,0.25)] !rounded-large bg-brandWhite !z-10">
                                {formatedHours &&
                                  formatedHours.map((hour, index) => (
                                    <Box
                                      key={index}
                                      className={`py-1 flex justify-between text-sm ${isToday(hour.day) && "font-bold"}`}
                                    >
                                      {hour.day}{" "}
                                      <Text className="relative bottom-[3px]">
                                        .
                                      </Text>{" "}
                                      {formatHours(hour.time)}
                                    </Box>
                                  ))}
                              </DropdownContent>
                            </DropdownMenu>
                          </Box>

                          {/* Store Address */}
                          <Text
                            as="p"
                            className="!text-body pb-4 flex gap-[8px] items-start"
                          >
                            <span className="[&_.icon-map]:before:w-6 [&_.icon-map]:before:h-6 [&_.icon-map]:before:content-['\e835']">
                              <Text
                                as="span"
                                className="icon-map flex text-xl w-6 h-6 justify-center items-center text-[#35A131]"
                              />
                            </span>
                            {`${store.address?.address1 || ""} ${store.address?.address2 || ""} 
                            ${store.address?.intersection || ""} ${store.address?.city || ""} 
                             ${store.address?.province || ""} ${store.address?.postalCode || ""}`}
                          </Text>

                          {/* Store Phone */}
                          <Text
                            as="p"
                            className="!text-body pb-4 flex gap-[8px] items-center"
                          >
                            <span className="[&_.icon-phone]:before:w-6 [&_.icon-phone]:before:h-6 [&_.icon-phone]:before:content-['\e840']">
                              <Text
                                as="span"
                                className="icon-phone flex text-xl w-6 h-6 justify-center items-center text-[#35A131]"
                              />
                            </span>
                            {store?.phoneDetails?.phone ? (
                              <a href={`tel:+${store.phoneDetails.phone}`}>
                                {formatPhoneNumber(store.phoneDetails.phone)}
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </Text>
                        </Box>
                        <Box className="xs:flex justify-between xs:gap-4 items-center pt-4 border-t border-[#DCDCDC]">
                          <Button
                            className={`text-[14px] xs:w-1/2 lgs:px-4 px-[10px] w-full ${
                              selectedStore?.storeNumber === store?.storeNumber
                                ? "cursor-not-allowed"
                                : ""
                            }`}
                            type="submit"
                            onClick={() => {
                              if (
                                selectedStore?.storeNumber !==
                                store?.storeNumber
                              ) {
                                openDialog();
                                setStoreToConfirm(store as IListStores);
                              }
                            }}
                            aria-label={
                              selectedStore?.storeNumber === store?.storeNumber
                                ? "Saved store"
                                : "Save as My Store"
                            }
                          >
                            {selectedStore?.storeNumber === store?.storeNumber
                              ? "Saved store"
                              : "Save as My Store"}
                          </Button>

                          <Button
                            variant="outline"
                            className="text-[14px] xs:w-1/2 w-full xs:mt-0 mt-3"
                            type="submit"
                            onClick={() =>
                              handleRedirectOnMap(
                                store?._geoloc?.lat,
                                store?._geoloc?.lng,
                                store?.locationName
                              )
                            }
                            aria-label={"Get Directions"}
                          >
                            Get Directions
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Columns>
            </Box>
          </Row>
        </Box>
      ) : null}
    </>
  );
};

export default NearByStores;
